import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MagnifyingGlass, CircleNotch, Airplane, ArrowRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const inputCls = "bg-black/40 border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 text-white font-sans";

/**
 * Flight Auto-fill via AeroDataBox (RapidAPI).
 *
 * Props:
 *   open        — boolean controlling dialog visibility
 *   onClose     — () => void
 *   onPick(seg) — called with the chosen flight segment; the parent decides
 *                 which fields to apply to the flight card
 *   defaultFlightNumber — pre-fills the flight # input from the existing card
 *   defaultDate         — pre-fills the date input (YYYY-MM-DD)
 */
export function FlightLookupDialog({ open, onClose, onPick, defaultFlightNumber = "", defaultDate = "" }) {
  const [flightNumber, setFlightNumber] = useState(defaultFlightNumber);
  const [date, setDate] = useState(defaultDate);
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null); // null | array
  const [error, setError] = useState("");

  // Reset state every time the dialog opens with new defaults
  React.useEffect(() => {
    if (open) {
      setFlightNumber(defaultFlightNumber);
      setDate(defaultDate);
      setMatches(null);
      setError("");
    }
  }, [open, defaultFlightNumber, defaultDate]);

  const search = async () => {
    const fn = flightNumber.trim().toUpperCase().replace(/\s+/g, "");
    const d = (date || "").slice(0, 10);
    if (!fn) {
      setError("Enter a flight number (e.g. CX873)");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      setError("Pick a flight date");
      return;
    }
    setLoading(true);
    setError("");
    setMatches(null);
    try {
      const { data } = await api.post("/flight-lookup", { flight_number: fn, date: d });
      setMatches(data.matches || []);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Lookup failed";
      if (status === 404) {
        setError("Flight not found, please enter manually.");
      } else if (status === 400) {
        setError(detail);
      } else if (status === 503) {
        setError(detail);
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const pick = (seg) => {
    onPick(seg);
    toast.success(`Filled ${seg.airline} ${seg.flight_number}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-2xl" data-testid="flight-lookup-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">
            <span className="italic text-[#D4AF37]">Flight</span> auto-fill
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs font-sans">
            Enter the flight number + date and we'll fetch airline, airports, and times. You can edit anything afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div className="sm:col-span-1">
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">Flight number</Label>
            <Input
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
              placeholder="CX873"
              className={inputCls}
              data-testid="lookup-flight-number-input"
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">Flight date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputCls}
              data-testid="lookup-flight-date-input"
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </div>
          <div className="sm:col-span-1 flex items-end">
            <Button
              onClick={search}
              disabled={loading}
              className="w-full h-10 bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest"
              data-testid="lookup-flight-search-btn"
            >
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <><MagnifyingGlass size={16} weight="duotone" /> Lookup</>}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-amber-400/30 bg-amber-500/10 px-4 py-3 flex items-start gap-2" data-testid="lookup-flight-error">
            <Warning size={16} weight="duotone" className="text-amber-300 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200/95 font-sans">{error}</p>
          </div>
        )}

        {matches && matches.length === 0 && !error && (
          <div className="mt-4 rounded-md border border-white/10 bg-black/30 px-4 py-6 text-center text-white/60 font-sans">
            No matches — try a different date or check the flight number.
          </div>
        )}

        {matches && matches.length > 0 && (
          <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="lookup-flight-results">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-sans">
              {matches.length === 1 ? "1 match" : `${matches.length} matches`} — click to use
            </p>
            {matches.map((seg, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pick(seg)}
                className="w-full text-left border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/5 transition-all rounded-lg p-4"
                data-testid={`lookup-flight-result-${i}`}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Airplane size={16} weight="duotone" className="text-[#D4AF37]" />
                    <span className="font-serif text-lg text-white">{seg.airline || "—"}</span>
                    <span className="text-[#D4AF37] font-mono text-sm">{seg.flight_number}</span>
                  </div>
                  {seg.status && (
                    <span className="text-[10px] uppercase tracking-widest text-white/50 font-sans">{seg.status}</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 font-sans">{seg.from_airport}</p>
                    <p className="font-serif text-xl text-white">{seg.from_city || "—"}</p>
                    <p className="text-xs text-white/60 font-sans mt-0.5">{seg.departure_date} <span className="text-[#D4AF37]">{seg.departure_time}</span></p>
                  </div>
                  <ArrowRight size={20} weight="bold" className="text-[#D4AF37]/60 mx-auto hidden sm:block" />
                  <div className="sm:text-right">
                    <p className="text-[10px] uppercase tracking-widest text-white/40 font-sans">{seg.to_airport}</p>
                    <p className="font-serif text-xl text-white">{seg.to_city || "—"}</p>
                    <p className="text-xs text-white/60 font-sans mt-0.5">{seg.arrival_date} <span className="text-[#D4AF37]">{seg.arrival_time}</span></p>
                  </div>
                </div>
                {(seg.aircraft || seg.duration_minutes) && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-3 text-[11px] text-white/50 font-sans">
                    {seg.aircraft && <span>✈ {seg.aircraft}</span>}
                    {seg.duration_minutes && <span>⏱ {Math.floor(seg.duration_minutes / 60)}h {seg.duration_minutes % 60}m</span>}
                    {seg.distance_km && <span>📍 {Math.round(seg.distance_km).toLocaleString()} km</span>}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
